import React from 'react';
import { Card } from '../../ui/Card';
import { sb } from '../../../modules/state';
import { useAuth } from '../../../context/AuthContext';

interface Event {
  id: string;
  user_id: string;
  event_date: string;
  event_type: string;
  note: string | null;
  created_at: string;
}

const EVENT_TYPES = {
  fast_start: { label: 'Fast Start', icon: 'play_arrow', color: 'text-primary' },
  fast_break: { label: 'Fast Break', icon: 'pause', color: 'text-secondary' },
  blood_test: { label: 'Blood Test Due', icon: 'water_drop', color: 'text-error' },
  target_weight: { label: 'Target Weight Date', icon: 'flag', color: 'text-primary' },
  milestone: { label: 'Personal Milestone', icon: 'emoji_events', color: 'text-success' },
  reminder: { label: 'Reminder', icon: 'notifications', color: 'text-on-surface' },
  note: { label: 'Note', icon: 'edit_note', color: 'text-on-surface-variant' },
};

export function ProgressTrackerTab() {
  const { currentUser } = useAuth();
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [events, setEvents] = React.useState<Event[]>([]);
  const [daysInSystem, setDaysInSystem] = React.useState<number>(0);
  const [nextEvent, setNextEvent] = React.useState<Event | null>(null);

  const [eventType, setEventType] = React.useState('fast_start');
  const [eventNote, setEventNote] = React.useState('');

  // Fetch data on mount
  React.useEffect(() => {
    const fetchData = async () => {
      if (!sb || !currentUser?.id) return;

      try {
        // Fetch user profile for created_at
        const { data: profileData, error: profileError } = await sb
          .from('profiles')
          .select('created_at')
          .eq('id', currentUser.id)
          .single();

        if (!profileError && profileData?.created_at) {
          const createdAt = new Date(profileData.created_at);
          const today = new Date();
          const diffTime = today.getTime() - createdAt.getTime();
          const diffDays = Math.floor(diffTime / 86400000);
          setDaysInSystem(diffDays > 0 ? diffDays : 0);
        }

        // Fetch all events for user
        const { data, error } = await sb
          .from('progress_events')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('event_date', { ascending: true });

        if (!error && data) {
          setEvents(data);
        }

        // Fetch next upcoming event
        const todayStr = new Date().toISOString().split('T')[0];
        const { data: nextEventData, error: nextEventError } = await sb
          .from('progress_events')
          .select('*')
          .eq('user_id', currentUser.id)
          .gte('event_date', todayStr)
          .order('event_date', { ascending: true })
          .limit(1)
          .single();

        if (!nextEventError && nextEventData) {
          setNextEvent(nextEventData);
        }
      } catch (error) {
        console.error('[ProgressTracker] Error fetching data:', error);
      }
    };

    fetchData();
  }, [currentUser]);

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getEventsForDate = (dateStr: string) => {
    return events.filter((e) => e.event_date === dateStr);
  };

  const getEventColor = (eventType: string) => {
    return EVENT_TYPES[eventType as keyof typeof EVENT_TYPES]?.color || 'text-on-surface';
  };

  const getEventIcon = (eventType: string) => {
    return EVENT_TYPES[eventType as keyof typeof EVENT_TYPES]?.icon || 'event';
  };

  // Navigate months
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Handle date tap
  const handleDayClick = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${dayStr}`);
    setEventType('fast_start');
    setEventNote('');
    setPanelOpen(true);
  };

  // Handle save event
  const handleSaveEvent = async () => {
    if (!sb || !currentUser?.id || !selectedDate) return;

    try {
      const { error } = await sb.from('progress_events').insert({
        user_id: currentUser.id,
        event_date: selectedDate,
        event_type: eventType,
        note: eventNote || null,
      });

      if (!error) {
        setPanelOpen(false);
        setSelectedDate(null);
        setEventNote('');
        // Refresh events
        const { data, error: fetchError } = await sb
          .from('progress_events')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('event_date', { ascending: true });

        if (!fetchError && data) {
          setEvents(data);
        }
      }
    } catch (error) {
      console.error('[ProgressTracker] Error saving event:', error);
    }
  };

  // Handle delete event
  const handleDeleteEvent = async (eventId: string) => {
    if (!sb) return;

    try {
      const { error } = await sb.from('progress_events').delete().eq('id', eventId);

      if (!error) {
        // Refresh events
        const { data, error: fetchError } = await sb
          .from('progress_events')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('event_date', { ascending: true });

        if (!fetchError && data) {
          setEvents(data);
        }
      }
    } catch (error) {
      console.error('[ProgressTracker] Error deleting event:', error);
    }
  };

  // Build calendar days
  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const days = [];

  // Empty cells for prev month
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="py-2" />);
  }

  const today = new Date().toISOString().split('T')[0];

  // Days of current month
  for (let day = 1; day <= daysInMonth; day++) {
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateStr = `${year}-${month}-${dayStr}`;
    const dayEvents = getEventsForDate(dateStr);
    const isToday = dateStr === today;
    const isPast = dateStr < today;

    days.push(
      <div
        key={day}
        onClick={() => handleDayClick(day)}
        className={`
          py-2 flex flex-col items-center justify-center rounded-lg transition-colors
          ${isToday ? 'bg-primary text-on-primary' : 'hover:bg-surface-container'}
          ${isPast ? 'opacity-40' : 'opacity-100'}
        `}
      >
        <span className="text-sm font-medium">{day}</span>
        {dayEvents.length > 0 && (
          <span className={`h-1 w-1 rounded-full mt-0.5 ${getEventColor(dayEvents[0].event_type)}`} />
        )}
      </div>
    );
  }

  // Month header
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  // Filter events for current month
  const currentMonthEvents = events.filter((e) => {
    const eventDate = new Date(e.event_date);
    return (
      eventDate.getMonth() === currentMonth.getMonth() &&
      eventDate.getFullYear() === currentMonth.getFullYear()
    );
  });

  return (
    <div className="flex h-full flex-col">
      {/* 1. Calendar Grid - Top of page, no padding above */}
      <div className="flex-shrink-0 w-full">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={prevMonth} className="p-2 hover:bg-surface rounded-lg">
            <span className="material-symbols-outlined text-on-surface">chevron_left</span>
          </button>
          <div className="text-lg font-semibold text-on-surface">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </div>
          <button onClick={nextMonth} className="p-2 hover:bg-surface rounded-lg">
            <span className="material-symbols-outlined text-on-surface">chevron_right</span>
          </button>
        </div>

        {/* Day Headers */}
        <div className="mb-2 grid grid-cols-7 gap-0 px-4 w-full">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <div key={day} className="text-center text-[10px] font-medium text-on-surface-variant">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-0 px-4 pb-4 w-full min-w-0">{days}</div>
      </div>

      {/* 2. Compact Widget Strip - Side by side below calendar */}
      <div className="grid grid-cols-2 gap-3 px-4 pb-3">
        {/* Days in System */}
        <Card className="flex items-center gap-3 p-3">
          <div className="flex h-8 w-8 flex-col items-center justify-center rounded-lg bg-primary/10">
            <span className="material-symbols-outlined text-primary text-base">event_available</span>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-on-surface-variant uppercase tracking-wide">
              Days in System
            </div>
            <div className="text-base font-bold text-on-surface">{daysInSystem}</div>
          </div>
        </Card>

        {/* Next Upcoming Event */}
        <Card className="flex items-center gap-3 p-3">
          <div className="flex h-8 w-8 flex-col items-center justify-center rounded-lg bg-surface-container">
            {nextEvent ? (
              <span
                className={`material-symbols-outlined ${getEventColor(nextEvent.event_type)} text-base`}
              >
                {getEventIcon(nextEvent.event_type)}
              </span>
            ) : (
              <span className="material-symbols-outlined text-on-surface-variant text-base">event_busy</span>
            )}
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-semibold text-on-surface-variant uppercase tracking-wide">
              Next Upcoming
            </div>
            {nextEvent ? (
              <div className="text-xs font-medium text-on-surface">
                {EVENT_TYPES[nextEvent.event_type as keyof typeof EVENT_TYPES]?.label || nextEvent.event_type}
                <span className="ml-1 text-[10px] text-on-surface-variant">
                  — {new Date(nextEvent.event_date).toLocaleDateString()}
                </span>
              </div>
            ) : (
              <div className="text-xs text-on-surface-variant">No upcoming events</div>
            )}
          </div>
        </Card>
      </div>

      {/* 3. Events List for Current Month */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="mb-3 text-xs font-semibold text-on-surface-variant uppercase tracking-wide">
          Events this month
        </div>

        {currentMonthEvents.length > 0 ? (
          <div className="space-y-2">
            {currentMonthEvents.map((event) => (
              <div key={event.id} className="flex items-center justify-between rounded-lg bg-surface-container px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className={`material-symbols-outlined ${getEventColor(event.event_type)} text-sm`}>
                    {getEventIcon(event.event_type)}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-on-surface">
                      {EVENT_TYPES[event.event_type as keyof typeof EVENT_TYPES]?.label || event.event_type}
                    </div>
                    {event.note && (
                      <div className="text-xs text-on-surface-variant">{event.note}</div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteEvent(event.id)}
                  className="p-1.5 hover:bg-error-container rounded"
                >
                  <span className="material-symbols-outlined text-error text-sm">delete</span>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <span className="material-symbols-outlined text-on-surface-variant text-4xl mb-2">
              event_busy
            </span>
            <p className="text-sm text-on-surface-variant">No events this month</p>
          </div>
        )}
      </div>

      {/* Slide-Up Panel */}
      {panelOpen && selectedDate && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-surface/80 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-t-2xl sm:rounded-xl bg-surface p-4 shadow-xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-on-surface">
                {selectedDate === today
                  ? 'Today'
                  : new Date(selectedDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
              </h3>
              <button
                onClick={() => setPanelOpen(false)}
                className="p-2 hover:bg-surface rounded-lg"
              >
                <span className="material-symbols-outlined text-on-surface-variant">close</span>
              </button>
            </div>

            {/* Existing Events */}
            {getEventsForDate(selectedDate).length > 0 && (
              <div className="mb-4 space-y-2">
                <div className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-2">
                  Existing Events
                </div>
                {getEventsForDate(selectedDate).map((event) => (
                  <div key={event.id} className="flex items-center justify-between p-2 bg-surface-container rounded-lg">
                    <div className="flex items-center gap-2">
                      <span
                        className={`material-symbols-outlined ${getEventColor(event.event_type)} text-lg`}
                      >
                        {getEventIcon(event.event_type)}
                      </span>
                      <span className="text-sm text-on-surface">
                        {EVENT_TYPES[event.event_type as keyof typeof EVENT_TYPES]?.label || event.event_type}
                      </span>
                      {event.note && (
                        <span className="text-xs text-on-surface-variant">: {event.note}</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="p-2 hover:bg-error-container rounded-lg"
                    >
                      <span className="material-symbols-outlined text-error text-sm">delete</span>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Event Type Dropdown */}
            <div className="mb-4 flex-1 overflow-y-auto">
              <label className="mb-2 block text-xs font-medium text-on-surface-variant">
                Event Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(EVENT_TYPES) as Array<keyof typeof EVENT_TYPES>).map((type) => (
                  <button
                    key={type}
                    onClick={() => setEventType(type)}
                    className={`
                      flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all
                      ${eventType === type
                        ? 'border-primary bg-primary/10'
                        : 'border-outline bg-surface hover:bg-surface-container'}
                    `}
                  >
                    <span className={`material-symbols-outlined ${getEventColor(type)}`}>
                      {EVENT_TYPES[type].icon}
                    </span>
                    <span className="text-on-surface">{EVENT_TYPES[type].label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Description Field */}
            <div className="mb-4">
              <label className="mb-2 block text-xs font-medium text-on-surface-variant">
                Note (optional)
              </label>
              <textarea
                value={eventNote}
                onChange={(e) => setEventNote(e.target.value)}
                placeholder="Add a note..."
                maxLength={120}
                className="w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm text-on-surface focus:border-primary focus:outline-none"
                rows={3}
              />
              <div className="mt-1 text-right text-[10px] text-on-surface-variant">
                {eventNote.length}/120
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => setPanelOpen(false)}
                className="flex-1 rounded-xl border border-outline bg-surface px-4 py-3 text-sm font-medium text-on-surface hover:bg-surface-container"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEvent}
                className="flex-1 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-contrast hover:shadow-lg hover:shadow-primary/30"
              >
                Save Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
